import { isTransientNetworkError } from '../utils/errorUtils.js';
import env from '../utils/environment.js';
import logger from '../utils/logger.js';
import supabase from '../utils/supabaseClient.js';

const CLOUDFLARE_EDGE_FUNCTIONS_URL = env.cloudflareEdgeFunctionsUrl;

/**
 * A service for making calls to Edge Functions via a Cloudflare worker.
 */
const apiService = {
  /**
   * Invokes an Edge Function, routing all calls through the Cloudflare endpoint.
   * This function handles both authenticated and public calls.
   *
   * @param {string} functionName - The name of the Edge Function to invoke.
   * @param {object} [options={}] - Options for the invocation, including `body` and `headers`.
   * @returns {Promise<any>} The response data from the Edge Function.
   */
  async invokeEdgeFunction(functionName, options = {}) {
    try {
      const { body, headers, method = 'POST', skipAuth = false, ...rest } = options;

      let jwt = null;
      if (!skipAuth) {
        const { data: { session } } = await supabase.auth.getSession();
        // If the token expires within 30s, proactively refresh to avoid
        // Supabase relay rejecting the request with 400 ("Invalid JWT")
        if (session?.expires_at && session.expires_at - Math.floor(Date.now() / 1000) < 30) {
          const { data: { session: refreshed } } = await supabase.auth.refreshSession();
          jwt = refreshed?.access_token ?? session?.access_token;
        } else {
          jwt = session?.access_token;
        }
      }

      const baseUrl = CLOUDFLARE_EDGE_FUNCTIONS_URL.endsWith('/')
        ? CLOUDFLARE_EDGE_FUNCTIONS_URL
        : `${CLOUDFLARE_EDGE_FUNCTIONS_URL}/`;
      const response = await fetch(`${baseUrl}${functionName}`, {
        method,
        headers: {
          ...(method !== 'GET' ? { 'Content-Type': 'application/json' } : {}),
          ...headers,
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        ...(method !== 'GET' && body ? { body: JSON.stringify(body) } : {}),
        ...rest,
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Attach structured metadata so callers can detect specific server-side
        // error codes (e.g. Step 5 `free_tier_model_cap` from persist-car-selection).
        const err = new Error(
          errorData.message ||
            errorData.error ||
            `Cloudflare Function ${functionName} failed with status ${response.status}`
        );
        err.status = response.status;
        err.code = errorData.error || null;
        err.body = errorData;
        throw err;
      }

      return await response.json();
    } catch (error) {
      if (isTransientNetworkError(error) && !options._retried) {
        logger.warn(`Transient network error for ${functionName}, retrying in 1s...`);
        await new Promise((r) => setTimeout(r, 1000));
        return this.invokeEdgeFunction(functionName, { ...options, _retried: true });
      }
      logger.error(`Error invoking Edge Function ${functionName} via Cloudflare:`, error);
      throw error;
    }
  },

  /**
   * Preinitiate a vehicle report generation job.
   * Checks for duplicate reports and creates a new report with async job.
   *
   * @param {string} modelId - The vehicle model ID to generate report for
   * @returns {Promise<{report_id: string, job_id: string, message?: string}>} Report and job details
   */
  async preinitiateReportJob(modelId) {
    return this.invokeEdgeFunction('preinitiate-report-job', {
      body: { model_id: modelId },
    });
  },

  /**
   * Get a download link for a completed vehicle report PDF.
   * Returns a Cloudflare CDN URL for the PDF with authentication handled by the CDN worker.
   *
   * @param {string} reportId - The ID of the report to download
   * @returns {Promise<{download_url: string}>} Cloudflare CDN URL for the PDF
   */
  async getReportDownloadLink(reportId) {
    return this.invokeEdgeFunction('get-report-download-link', {
      body: { report_id: reportId },
    });
  },

  /**
   * Retrigger a failed vehicle report generation.
   * Creates a new async job to regenerate the report.
   *
   * @param {string} reportId - The ID of the failed report to retry
   * @returns {Promise<{message: string, job_id: string}>} Success message and new job ID
   */
  async retriggerFailedReport(reportId) {
    return this.invokeEdgeFunction('retrigger-failed-report', {
      body: { report_id: reportId },
    });
  },

  // --- Listings Feed Methods ---

  /**
   * Get cached listing feed data for a specific vehicle model.
   * Returns scored listings from the user's daily feed.
   *
   * @param {string} modelId - The vehicle model ID to get listings for
   * @returns {Promise<{listings: Array, totalCount: number, feedGeneratedAt: string}>} Feed data
   */
  async getModelListings(modelId) {
    return this.invokeEdgeFunction('get-model-listings', {
      body: { model_id: modelId },
    });
  },

  /**
   * Get full listing details from Marketcheck for a specific listing.
   * Proxies the Marketcheck API to keep the API key server-side.
  /**
   * Record a user interaction with a listing (save, dismiss, view, share, etc.).
   * Also updates feed state (saved_at, dismissed_at, viewed_at) via service_role.
   *
   * @param {string} feedEntryId - The feed entry UUID (user_listing_feeds.id)
   * @param {string} actionType - One of: save, dismiss, view, share, finance_click, compare
   * @param {Object} [metadata] - Optional metadata for the interaction
   * @returns {Promise<{success: boolean, feed_entry_id: string, vdp_url: string, marketcheck_id: string}>} Confirmation
   */
  async recordListingInteraction(feedEntryId, actionType, metadata) {
    return this.invokeEdgeFunction('listing-interaction', {
      body: { feed_entry_id: feedEntryId, action_type: actionType, metadata },
    });
  },

  /**
   * Submit a finance lead for a listing.
   * Stores the lead with listing snapshot for dealer follow-up.
   *
   * @param {string} feedEntryId - The user_listing_feeds entry UUID
   * @param {Object} formData - Finance form data (deposit_amount, preferred_monthly_budget, notes)
   * @returns {Promise<{lead_id: string}>} The created lead ID
   */
  async submitFinanceLead(feedEntryId, formData) {
    return this.invokeEdgeFunction('submit-finance-lead', {
      body: { feed_entry_id: feedEntryId, form_data: formData },
    });
  },

  /**
   * Create a shareable snapshot of a listing from the user's feed.
   * Returns a share token and URL for the listing snapshot page.
   *
   * @param {string} feedEntryId - The user_listing_feeds entry ID
   * @returns {Promise<{token: string, share_url: string}>} Share token and URL
   */
  async createListingSnapshot(feedEntryId) {
    return this.invokeEdgeFunction('create-listing-snapshot', {
      body: { feed_entry_id: feedEntryId },
    });
  },

  /**
   * Trigger on-demand feed generation for the authenticated user.
   * Used when a reactivated user's feed is stale (>24h).
   *
   * @returns {Promise<{job_id?: string, feed?: Object}>} 202 with job_id if stale, 200 with feed if fresh
   */
  async triggerListingFeed() {
    return this.invokeEdgeFunction('trigger-listing-feed', {
      body: {},
    });
  },

  // --- Redesign V2: Unified Feed Methods ---

  /**
   * Get the unified listings feed with optional model filter and cursor pagination.
   *
   * @param {Object} options - Feed query options
   * @param {string} [options.model_id] - Filter by model UUID
   * @param {string} [options.cursor] - Base64-encoded cursor for pagination
   * @param {number} [options.limit=20] - Number of listings per page (max 100)
   * @param {boolean} [options.favorites_only=false] - Only return saved listings
   * @returns {Promise<{listings: Array, nextCursor: string|null, totalCount: number, models: Array}>}
   */
  async getUnifiedFeed({ model_id, cursor, limit, favorites_only, source_filter } = {}) {
    return this.invokeEdgeFunction('get-unified-listings-feed', {
      body: { model_id, cursor, limit, favorites_only, source_filter },
    });
  },

  /**
   * Get full listing details by feed entry ID.
   * Returns feed entry data, Marketcheck extra data (from cache), and market averages.
   *
   * @param {string} feedEntryId - The user_listing_feeds entry ID
   * @returns {Promise<{listing: Object, extra: Object|null, market_averages: Object|null}>}
   */
  async getListingDetailByFeedEntry(feedEntryId) {
    return this.invokeEdgeFunction('get-listing-detail', {
      body: { feed_entry_id: feedEntryId },
    });
  },

  /**
   * Persist the user's car selection (selected models for feed tracking).
   * Resets all selections then sets the provided model IDs.
   *
   * @param {string[]} modelIds - Array of model UUIDs (max 5)
   * @returns {Promise<{success: boolean}>}
   */
  async persistCarSelection(modelIds) {
    return this.invokeEdgeFunction('persist-car-selection', {
      body: { model_ids: modelIds },
    });
  },

  /**
   * Trigger listing feed generation for specific models.
   * Used after car selection to start scanning for listings.
   *
   * @param {string[]} modelIds - Array of model UUIDs to trigger feeds for
   * @returns {Promise<{job_id?: string}>}
   */
  async triggerListingFeedForModels(modelIds) {
    return this.invokeEdgeFunction('trigger-listing-feed', {
      body: { model_ids: modelIds },
    });
  },

  /**
   * Get hero carousel listings for the landing page.
   * Public endpoint, no auth required.
   *
   * @returns {Promise<{cards: Array}>} 5 high-scoring display cards
   */
  async getHeroListings() {
    return this.invokeEdgeFunction('get-hero-listings', {
      body: {},
    });
  },

  // --- Negotiation Methods ---

  /**
   * Request a negotiation action (create, generate draft, regenerate, send, follow-up).
   * Routes through the request-negotiation-action edge function which validates
   * ownership, limits, and inserts the appropriate async job.
   *
   * @param {string} action - One of: create_negotiation, generate_draft, regenerate_draft, send_approved_draft, generate_followup
   * @param {Object} payload - Action-specific payload (e.g. negotiation_id, message_id, setup_config)
   * @returns {Promise<{job_id: string, message?: string}>} Job details
   */
  async requestNegotiationAction(action, payload) {
    return this.invokeEdgeFunction('request-negotiation-action', {
      body: { action, ...payload },
    });
  },

  /**
   * Look up an external listing by URL (paste-a-link).
   * Creates an async job that resolves via Marketcheck VDP lookup.
   *
   * @param {string} url - The external listing URL to look up
   * @returns {Promise<{job_id: string}>} The async job ID to monitor for results
   */
  async lookupExternalListing(url) {
    return this.invokeEdgeFunction('lookup-external-listing', {
      body: { action: 'lookup', url },
    });
  },

  /**
   * Confirm or decline adding a model from a paste-a-link lookup.
   *
   * @param {string} jobId - The original lookup async job ID
   * @param {'add'|'decline'} decision - User's decision
   * @returns {Promise<{success: boolean, job_id?: string, declined?: boolean}>}
   */
  async confirmModelAdd(jobId, decision) {
    return this.invokeEdgeFunction('lookup-external-listing', {
      body: { action: 'confirm_model_add', job_id: jobId, user_decision: decision },
    });
  },

  /**
   * Resolve a presigned download URL for a dealer-attachment via the
   * negotiation-attachment-download edge function (path B2). The function
   * verifies ownership and returns a short-lived signed S3 URL the browser
   * can hit directly.
   *
   * @param {string} messageId
   * @param {number} idx - attachment index in negotiation_messages.attachments
   * @returns {Promise<{ url: string, filename: string, ttl: number }>}
   */
  async getNegotiationAttachmentUrl(messageId, idx) {
    // Routes through invokeEdgeFunction (project rule: never hand-roll fetch
    // against /functions/v1/) so JWT refresh, transient-error retry, and
    // structured error metadata (.status / .code / .body) come for free.
    // Query params are appended to the function name; _t cache-buster keeps
    // the Cloudflare proxy from returning a stale presigned URL.
    const qs = `message_id=${encodeURIComponent(messageId)}&idx=${encodeURIComponent(String(idx))}&_t=${Date.now()}`;
    const body = await this.invokeEdgeFunction(`negotiation-attachment-download?${qs}`, {
      method: 'GET',
      headers: { 'Cache-Control': 'no-store' },
    });
    if (!body?.url) throw new Error('No URL returned');
    return { url: body.url, filename: body.filename || 'attachment', ttl: body.ttl || 60 };
  },

  /**
   * File an internal-only report on an AI-generated draft. Used during beta
   * to flag bad drafts for prompt iteration.
   *
   * @param {string} messageId - The negotiation_messages.id being reported
   * @param {'wrong_price'|'wrong_tone'|'factually_wrong'|'leaked_info'|'other'} reason
   * @param {string} [freeText] - Optional free-text note from the user
   * @returns {Promise<{ ok: boolean, report_id: string }>}
   */
  async reportDraft(messageId, reason, freeText) {
    return this.invokeEdgeFunction('report-draft', {
      body: { message_id: messageId, reason, free_text: freeText || undefined },
    });
  },
};

export { apiService, supabase };
